import React from 'react'
import { useT, useI18n } from '../../lib/i18n'
import { useApp } from '../../lib/context'
import { useProjects, useAggregate, useAlerts, type Bucket } from '../../lib/api'
import Sparkline from '../Sparkline'
import SystemBanner from '../SystemBanner'
import type { ProjectStatus } from '../../lib/types'

/* Mapping health → statut projet */
function healthToStatus(health: string): ProjectStatus {
  if (health === 'warning')  return 'waiting'
  if (health === 'critical') return 'stopped'
  return 'active'
}

/* Fond de pill actif — rgba fallback (pas de color-mix pour compat Safari < 16.2) */
const PILL_BG: Record<ProjectStatus, string> = {
  all:     'rgba(144,144,176,0.12)',
  active:  'rgba(34,197,94,0.13)',
  waiting: 'rgba(245,158,11,0.13)',
  stopped: 'rgba(239,68,68,0.13)',
}

const STATUS_FILTER_CONFIG: { id: ProjectStatus; label: string; labelEn: string; color: string }[] = [
  { id: 'all',     label: 'Tous',       labelEn: 'All',     color: 'var(--text-muted)' },
  { id: 'active',  label: 'Actifs',     labelEn: 'Active',  color: 'var(--health-good)' },
  { id: 'waiting', label: 'En attente', labelEn: 'Waiting', color: 'var(--health-warn)' },
  { id: 'stopped', label: 'En arrêt',   labelEn: 'Stopped', color: 'var(--health-crit)' },
]

const MOCK_SPARKLINES: Record<string, number[]> = {
  erp_kaytech:       [12, 18, 22, 19, 26, 31, 28, 35, 42, 38, 44, 52],
  kaytechnologie:    [8,  11, 9,  14, 16, 13, 18, 22, 19, 24, 26, 28],
  erp_assg:          [15, 18, 16, 22, 19, 25, 23, 28, 31, 29, 33, 36],
  erp_supmanagement: [32, 28, 35, 41, 38, 44, 39, 46, 52, 48, 55, 49],
  tontine_app:       [10, 14, 18, 22, 19, 26, 31, 28, 34, 38, 41, 44],
  erp_agasa:         [18, 22, 19, 24, 21, 26, 23, 28, 25, 30, 27, 32],
  cumcco_ga:         [6,  9,  12, 14, 18, 16, 21, 24, 22, 27, 29, 31],
}

const HEALTH_LABEL: Record<string, string> = {
  good:     'OK',
  warning:  'Alerte',
  critical: 'Critique',
}

/* ── Carte KPI globale ────────────────────────────────────────── */
function KpiCard({ value, label, color = 'var(--text)', sub }: {
  value: number | string; label: string; color?: string; sub?: string
}) {
  return (
    <div className="animate-in" style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '16px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: 0,
    }}>
      <div style={{
        fontSize: 'var(--text-2xl)',
        fontWeight: 800,
        fontFamily: 'var(--font-mono)',
        color,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      }}>
        {value}
      </div>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', fontWeight: 500 }}>
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
          {sub}
        </div>
      )}
    </div>
  )
}

/* ── Chip santé ───────────────────────────────────────────────── */
function HealthChip({ health }: { health: string }) {
  return (
    <span className={`health-chip ${health}`}>
      <span className="health-dot" />
      {HEALTH_LABEL[health] ?? health}
    </span>
  )
}

/* ── Barre de progression inline ─────────────────────────────── */
function InlineProgress({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, Math.round(value / max * 100)) : 0
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div className="progress-track" style={{ height: '5px', flex: 1, minWidth: 60 }}>
        <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)', color, fontWeight: 700, minWidth: 32, textAlign: 'right' }}>
        {pct}%
      </span>
    </div>
  )
}

export default function Overview() {
  const t = useT()
  const { lang } = useI18n()
  const { period, statusFilter, setStatusFilter } = useApp()
  const { data: projData, loading: projLoading } = useProjects()
  const { data: aggData, loading: aggLoading } = useAggregate(period)
  const { data: alertsData } = useAlerts()

  const loading = projLoading || aggLoading
  const alerts  = alertsData?.alerts ?? []

  const bucketMap = new Map<string, Bucket>(
    (aggData?.buckets ?? []).map((b) => [b.projectId, b])
  )

  if (loading && !projData) {
    return (
      <div style={{ padding: '32px', color: 'var(--text-muted)', fontSize: 'var(--text-base)' }}>
        Chargement…
      </div>
    )
  }

  const projects = projData?.projects ?? []

  /* KPIs globaux (toujours sur tous les projets) */
  const totalActions   = projects.reduce((s, p) => s + p.actions, 0)
  const totalErrors    = projects.reduce((s, p) => s + p.errors, 0)
  const totalCompleted = projects.reduce((s, p) => s + p.completed, 0)
  const totalCommits   = projects.reduce((s, p) => s + p.commits, 0)
  const healthCrit     = projects.filter(p => (bucketMap.get(p.id)?.health ?? 'good') === 'critical').length
  const countActive    = projects.filter(p => healthToStatus(bucketMap.get(p.id)?.health ?? 'good') === 'active').length
  const countWaiting   = projects.filter(p => healthToStatus(bucketMap.get(p.id)?.health ?? 'good') === 'waiting').length
  const countStopped   = projects.filter(p => healthToStatus(bucketMap.get(p.id)?.health ?? 'good') === 'stopped').length

  /* Projets filtrés selon le statut sélectionné */
  const filteredProjects = statusFilter === 'all'
    ? projects
    : projects.filter(p => healthToStatus(bucketMap.get(p.id)?.health ?? 'good') === statusFilter)

  const counts: Record<ProjectStatus, number> = {
    all: projects.length, active: countActive, waiting: countWaiting, stopped: countStopped,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* ── Bloc 1+2 : Verdict système + Actions prioritaires ─── */}
      <SystemBanner
        buckets={(aggData?.buckets ?? []).map((b) => ({ projectId: b.projectId, health: b.health }))}
        alerts={alerts}
        totalAlerts={alerts.filter((a) => !a.ackedAt && (a.severity === 'critical' || a.severity === 'high')).length}
        criticalProjects={healthCrit}
        totalErrors={totalErrors}
      />

      {/* ── Barre KPIs globaux ────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px',
      }}>
        <KpiCard value={projects.length} label={t('projects')} />
        <KpiCard value={totalCompleted} label={t('completed')} color="var(--health-good)" />
        <KpiCard value={totalActions}   label={t('actions')} />
        <KpiCard value={totalCommits}   label={t('commits')} color="var(--chart-git)" />
        <KpiCard
          value={totalErrors}
          label={t('errors')}
          color={totalErrors > 0 ? 'var(--health-crit)' : 'var(--text-muted)'}
          sub={totalErrors > 0 ? `${healthCrit} projet(s) critique(s)` : 'Aucune erreur'}
        />
      </div>

      {/* ── Table projets ─────────────────────────────────────── */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}>

        {/* En-tête table + filtres statut */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          gap: '8px',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--text)' }}>
            Vue d'ensemble des projets
          </span>

          {/* Filtres statut */}
          <div style={{ display: 'flex', gap: '6px', marginLeft: '12px', flexWrap: 'wrap' }}>
            {STATUS_FILTER_CONFIG.map(({ id, label, labelEn, color }) => (
              <button
                key={id}
                onClick={() => setStatusFilter(id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  border: `1.5px solid ${statusFilter === id ? color : 'var(--border)'}`,
                  background: statusFilter === id ? PILL_BG[id] : 'transparent',
                  color: statusFilter === id ? color : 'var(--text-muted)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                  outline: 'none',
                }}
              >
                <span style={{
                  width: 7, height: 7,
                  borderRadius: '50%',
                  background: color,
                  flexShrink: 0,
                }} />
                {lang === 'fr' ? label : labelEn}
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '10px',
                  opacity: 0.8,
                }}>
                  {counts[id]}
                </span>
              </button>
            ))}
          </div>

          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>
            {filteredProjects.length} / {projects.length} projet{projects.length > 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ overflowX: 'auto', overscrollBehaviorX: 'contain' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-base)' }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)' }}>
                <th style={thStyle}>Santé</th>
                <th style={thStyle}>Projet</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Complété</th>
                <th style={{ ...thStyle, minWidth: 130 }}>Progression</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Erreurs</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Commits</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Tendance</th>
                <th style={{ ...thStyle, width: 90 }}>Activité</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length === 0 && (
                <tr>
                  <td colSpan={9} style={{
                    padding: '48px 20px',
                    textAlign: 'center',
                    color: 'var(--text-dim)',
                    fontSize: 'var(--text-base)',
                    fontStyle: 'italic',
                  }}>
                    Aucun projet dans cette catégorie
                  </td>
                </tr>
              )}
              {filteredProjects.map((p, i) => {
                const b = bucketMap.get(p.id)
                const health = b?.health ?? 'good'
                const trend  = b?.trend  ?? 0
                const m      = b?.totals
                const sparkline = MOCK_SPARKLINES[p.id] ?? []

                const completed   = m?.completed ?? 0
                const actions     = m?.actions   ?? 0
                const errors      = m?.errors    ?? 0
                const commits     = m?.commits   ?? 0
                const successRate = m?.successRate ?? (errors === 0 ? 100 : 0)

                const progressColor =
                  health === 'critical' ? 'var(--health-crit)' :
                  health === 'warning'  ? 'var(--health-warn)' :
                  'var(--health-good)'

                return (
                  <tr
                    key={p.id}
                    className="animate-in"
                    style={{ borderBottom: '1px solid var(--border)', animationDelay: `${i * 0.04}s`, transition: 'background 120ms' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    {/* Santé */}
                    <td style={tdStyle}>
                      <HealthChip health={health} />
                    </td>

                    {/* Projet */}
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 'var(--text-md)' }}>
                        {p.name}
                      </div>
                      {p.lastActivity && (
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                          {p.lastActivity.slice(0, 10)}
                        </div>
                      )}
                    </td>

                    {/* Complété */}
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--health-good)' }}>
                      {completed}
                    </td>

                    {/* Progression */}
                    <td style={{ ...tdStyle, minWidth: 130 }}>
                      <InlineProgress value={successRate} max={100} color={progressColor} />
                    </td>

                    {/* Actions */}
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                      {actions}
                    </td>

                    {/* Erreurs */}
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: errors > 0 ? 700 : 400 }}>
                      <span style={{ color: errors > 0 ? 'var(--health-crit)' : 'var(--text-dim)' }}>
                        {errors > 0 ? `⚠ ${errors}` : '—'}
                      </span>
                    </td>

                    {/* Commits */}
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)', color: commits > 0 ? 'var(--chart-git)' : 'var(--text-dim)' }}>
                      {commits > 0 ? commits : '—'}
                    </td>

                    {/* Tendance */}
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                      <span style={{ color: trend >= 0 ? 'var(--health-good)' : 'var(--health-crit)' }}>
                        {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
                      </span>
                    </td>

                    {/* Sparkline */}
                    <td style={{ ...tdStyle, padding: '4px 12px' }}>
                      <Sparkline
                        data={sparkline}
                        color={progressColor}
                        type="area"
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 'var(--text-xs)' as string,
  fontWeight: 700,
  color: 'var(--text-dim)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.07em',
  whiteSpace: 'nowrap' as const,
}
const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  verticalAlign: 'middle',
}
